import type { FormEvent } from 'react';
import { useState } from 'react';

import type { GameCatalogEntry } from '@perplexicon/api-client';

import {
  Button,
  Dialog,
  ErrorState,
  Input,
  LoadingState,
  Tabs,
  UserCard,
} from '../../components/index.js';
import { useCreateMultiplayerSession } from '../../lib/games.js';
import { isActiveGameConflict } from '../../lib/active-game-conflict.js';
import { getErrorMessage } from '../../lib/errors.js';
import { useFriends } from '../../lib/friends.js';
import { useUserSearch } from '../../lib/users.js';
import { modeLabel } from './mode-labels.js';

export interface CreateMultiplayerDialogProps {
  game: GameCatalogEntry;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called once an invitation has been sent, so the caller can navigate/toast/refresh. */
  onInvited?: () => void;
  /**
   * Passed down from the page so a start/join refused with
   * `ACTIVE_SESSION_EXISTS` (APP_SPEC §21.1) opens the shared
   * resume-or-abandon dialog instead of this dialog's own error banner.
   * Returns `true` when it handled the error.
   */
  onActiveGameConflict?: (error: unknown, retry: () => void) => boolean;
}

/**
 * The create-multiplayer-game flow (APP_SPEC §19-§20): choose one of the
 * game's own declared non-solo modes, then pick an opponent — from
 * friends or by username search — and send the invitation. Mode and
 * player-count options come entirely from `game.supportedModes` /
 * `game.playerCounts` (the API's catalog response), never a hardcoded
 * list (CLAUDE.md §2). Only modes whose declared player count could ever
 * be satisfied by "the creator plus one named opponent" (min <= 2 <= max)
 * are offered here — the same constraint the server enforces.
 */
export function CreateMultiplayerDialog({
  game,
  open,
  onOpenChange,
  onInvited,
  onActiveGameConflict,
}: CreateMultiplayerDialogProps) {
  // Never offered for a game declaring `joinModel: 'open_lobby'`
  // (docs/games/word-rush/GAME_PLAN.md §13.2): its single-opponent invite
  // flow is a server-side dead end. `GameCard.tsx`'s `canPlayVersus` already
  // keeps this dialog from being opened for such a game at all; this is a
  // second, defense-in-depth guard.
  const invitableModes =
    game.joinModel === 'open_lobby'
      ? []
      : game.supportedModes.filter((candidate) => {
          if (candidate === 'solo') return false;
          const counts = game.playerCounts[candidate];
          return !!counts && counts.min <= 2 && counts.max >= 2;
        });
  const [mode, setMode] = useState(invitableModes[0] ?? '');
  const [tab, setTab] = useState('friends');
  const [query, setQuery] = useState('');
  const [submittedQuery, setSubmittedQuery] = useState('');

  const friends = useFriends();
  const search = useUserSearch(submittedQuery);
  const createMultiplayer = useCreateMultiplayerSession();

  function invite(username: string) {
    createMultiplayer.mutate(
      { gameId: game.gameId, mode, opponentUsername: username },
      {
        onSuccess: () => {
          onInvited?.();
          onOpenChange(false);
        },
        // Stays open behind the shared conflict dialog so the retry can
        // re-run this mutation from a still-mounted component.
        onError: (error) => onActiveGameConflict?.(error, () => invite(username)),
      },
    );
  }

  function onSearchSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmittedQuery(query.trim());
  }

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={`Play ${game.name} vs a friend`}
      description="Choose a mode and an opponent. They'll receive an invitation to accept."
    >
      <div className="flex flex-col gap-4">
        {invitableModes.length > 1 && (
          <div className="flex flex-col gap-1">
            <label htmlFor="multiplayer-mode" className="text-sm font-medium text-textPrimary">
              Mode
            </label>
            <select
              id="multiplayer-mode"
              value={mode}
              onChange={(event) => setMode(event.target.value)}
              className="rounded-md border border-border bg-surface px-3 py-2 text-base text-textPrimary"
            >
              {invitableModes.map((candidate) => (
                <option key={candidate} value={candidate}>
                  {modeLabel(candidate)}
                </option>
              ))}
            </select>
          </div>
        )}

        {invitableModes.length === 0 ? (
          <p className="text-textSecondary">This game has no two-player mode available.</p>
        ) : (
          <Tabs
            aria-label="Choose an opponent"
            value={tab}
            onValueChange={setTab}
            items={[
              {
                value: 'friends',
                label: 'Friends',
                content: (
                  <div className="flex flex-col gap-2">
                    {friends.isPending && <LoadingState label="Loading friends…" />}
                    {friends.isError && <ErrorState message={getErrorMessage(friends.error)} />}
                    {friends.isSuccess && friends.data.length === 0 && (
                      <p className="text-textSecondary">
                        You don&apos;t have any friends yet — try Search instead.
                      </p>
                    )}
                    {friends.isSuccess &&
                      friends.data.map((friend) => (
                        <UserCard
                          key={friend.user.id}
                          user={friend.user}
                          actions={
                            <Button
                              size="sm"
                              isLoading={
                                createMultiplayer.isPending &&
                                createMultiplayer.variables?.opponentUsername ===
                                  friend.user.username
                              }
                              disabled={createMultiplayer.isPending}
                              onClick={() => invite(friend.user.username)}
                            >
                              Invite
                            </Button>
                          }
                        />
                      ))}
                  </div>
                ),
              },
              {
                value: 'search',
                label: 'Find by username',
                content: (
                  <div className="flex flex-col gap-4">
                    <form onSubmit={onSearchSubmit} className="flex items-end gap-2">
                      <div className="flex-1">
                        <Input
                          label="Username"
                          placeholder="Search by username"
                          value={query}
                          onChange={(event) => setQuery(event.target.value)}
                        />
                      </div>
                      <Button type="submit">Search</Button>
                    </form>
                    {search.isPending && submittedQuery.length > 0 && (
                      <LoadingState label="Searching…" />
                    )}
                    {search.isError && <ErrorState message={getErrorMessage(search.error)} />}
                    {search.isSuccess && submittedQuery.length > 0 && search.data.length === 0 && (
                      <p className="text-textSecondary">
                        No players found for &quot;{submittedQuery}&quot;.
                      </p>
                    )}
                    {search.isSuccess &&
                      search.data.map((result) => (
                        <UserCard
                          key={result.user.id}
                          user={result.user}
                          actions={
                            <Button
                              size="sm"
                              isLoading={
                                createMultiplayer.isPending &&
                                createMultiplayer.variables?.opponentUsername ===
                                  result.user.username
                              }
                              disabled={createMultiplayer.isPending}
                              onClick={() => invite(result.user.username)}
                            >
                              Invite
                            </Button>
                          }
                        />
                      ))}
                  </div>
                ),
              },
            ]}
          />
        )}

        {createMultiplayer.isError && !isActiveGameConflict(createMultiplayer.error) && (
          <ErrorState message={getErrorMessage(createMultiplayer.error)} />
        )}
      </div>
    </Dialog>
  );
}
